import React from 'react';
import csvFormatImage from '../../assets/documentation/csv-format.png';
import jsonFormatImage from '../../assets/documentation/json-format.png';

const DataFormatGuide = () => {
  return (
    <div className="data-format-guide">
      <h2>Data Format Guide</h2>
      <p className="section-intro">
        RangeDat accepts data in multiple formats. This guide explains the supported formats and 
        requirements for successful data imports.
      </p>

      <div className="format-section">
        <h3>CSV Format</h3>
        <p>
          CSV (Comma-Separated Values) is the most common format for launch monitor exports. 
          RangeDat supports CSV files with the following specifications:
        </p>

        <div className="format-details">
          <div className="format-info">
            <h4>Requirements</h4>
            <ul>
              <li>File must have a <code>.csv</code> extension</li>
              <li>First row should contain column headers</li>
              <li>Data should be comma-separated</li>
              <li>Text fields containing commas should be enclosed in double quotes</li>
              <li>Date fields should be in ISO format (YYYY-MM-DD) or MM/DD/YYYY format</li>
              <li>Time fields should be in 24-hour format (HH:MM:SS)</li>
            </ul>

            <h4>Required Columns</h4>
            <p>
              At minimum, your CSV file should include the following columns (column names may vary):
            </p>
            <ul>
              <li>Club (e.g., "Driver", "7 Iron")</li>
              <li>Ball Speed (in mph or kph)</li>
              <li>Distance (carry, total, or both)</li>
            </ul>

            <h4>Recommended Additional Columns</h4>
            <p>
              For best results and more detailed analysis, include these columns if available:
            </p>
            <ul>
              <li>Club Speed</li>
              <li>Smash Factor</li>
              <li>Launch Angle</li>
              <li>Launch Direction</li>
              <li>Spin Rate</li>
              <li>Spin Axis</li>
              <li>Apex Height</li>
              <li>Descent Angle</li>
              <li>Carry Distance</li>
              <li>Total Distance</li>
              <li>Offline Distance</li>
              <li>Shot Time/Date</li>
            </ul>
          </div>

          <div className="format-example">
            <h4>Example CSV Format</h4>
            <div className="code-block">
              <pre>
{`Shot,Club,Ball Speed,Club Speed,Smash Factor,Launch Angle,Launch Direction,Spin Rate,Spin Axis,Carry,Total,Offline
1,Driver,152.3,105.6,1.44,13.2,2.1,2450,-3.2,245.6,267.8,8.3
2,Driver,155.7,107.2,1.45,12.8,-1.5,2380,2.1,251.2,275.4,-5.2
3,7 Iron,118.5,83.2,1.42,18.7,0.8,6540,1.5,158.3,162.1,2.1
4,7 Iron,120.1,84.5,1.42,19.2,-0.5,6720,-0.8,162.5,165.8,-1.4`}
              </pre>
            </div>
            <div className="image-container">
              <img 
                src={csvFormatImage} 
                alt="CSV Format Example" 
                className="format-image"
              />
              <p className="image-caption">Example of a properly formatted CSV file</p>
            </div>
          </div>
        </div>
      </div>

      <div className="format-section">
        <h3>JSON Format</h3>
        <p>
          JSON (JavaScript Object Notation) is supported for more advanced integrations. 
          RangeDat accepts JSON files with the following structure:
        </p>

        <div className="format-details">
          <div className="format-info">
            <h4>Requirements</h4>
            <ul>
              <li>File must have a <code>.json</code> extension</li>
              <li>Must be valid JSON format</li>
              <li>Should contain a session object with metadata and an array of shots</li>
              <li>Each shot should be an object with consistent properties</li>
            </ul>

            <h4>Required Properties</h4>
            <p>
              At minimum, your JSON file should include:
            </p>
            <ul>
              <li>Session metadata (date, location, etc.)</li>
              <li>Array of shots with club, ball speed, and distance information</li>
            </ul>

            <h4>Recommended Structure</h4>
            <p>
              For optimal compatibility, structure your JSON as shown in the example.
            </p>
          </div>

          <div className="format-example">
            <h4>Example JSON Format</h4>
            <div className="code-block">
              <pre>
{`{
  "session": {
    "date": "2023-06-15",
    "time": "14:30:00",
    "location": "Driving Range",
    "launchMonitor": "Trackman",
    "notes": "Working on driver swing changes"
  },
  "shots": [
    {
      "id": 1,
      "club": "Driver",
      "ballSpeed": 152.3,
      "clubSpeed": 105.6,
      "smashFactor": 1.44,
      "launchAngle": 13.2,
      "launchDirection": 2.1,
      "spinRate": 2450,
      "spinAxis": -3.2,
      "carry": 245.6,
      "total": 267.8,
      "offline": 8.3,
      "timestamp": "2023-06-15T14:32:15"
    },
    {
      "id": 2,
      "club": "Driver",
      "ballSpeed": 155.7,
      "clubSpeed": 107.2,
      "smashFactor": 1.45,
      "launchAngle": 12.8,
      "launchDirection": -1.5,
      "spinRate": 2380,
      "spinAxis": 2.1,
      "carry": 251.2,
      "total": 275.4,
      "offline": -5.2,
      "timestamp": "2023-06-15T14:33:45"
    }
  ]
}`}
              </pre>
            </div>
            <div className="image-container">
              <img 
                src={jsonFormatImage} 
                alt="JSON Format Example" 
                className="format-image"
              />
              <p className="image-caption">Example of a properly formatted JSON file</p>
            </div>
          </div>
        </div>
      </div>

      <div className="format-section">
        <h3>Excel Format</h3>
        <p>
          RangeDat also supports Excel files (.xlsx, .xls) with similar structure to CSV files.
        </p>

        <div className="format-details">
          <div className="format-info">
            <h4>Requirements</h4>
            <ul>
              <li>File must have a <code>.xlsx</code> or <code>.xls</code> extension</li>
              <li>First sheet will be used for data import</li>
              <li>First row should contain column headers</li>
              <li>Data should be organized in a tabular format</li>
            </ul>

            <h4>Tips for Excel Files</h4>
            <ul>
              <li>Avoid merged cells</li>
              <li>Remove any charts, images, or formatting</li>
              <li>Ensure data types are consistent (numbers for numeric values, etc.)</li>
              <li>Remove any empty rows or columns</li>
            </ul>
          </div>
        </div>
      </div>

      <div className="format-section">
        <h3>Custom Format Mapping</h3>
        <p>
          If your data doesn't match our standard formats, RangeDat provides a mapping tool during import.
        </p>

        <div className="mapping-info">
          <h4>How Mapping Works</h4>
          <ol>
            <li>Upload your file in any supported format</li>
            <li>RangeDat will analyze the file structure and display column headers</li>
            <li>You'll be prompted to map your columns to RangeDat's standard fields</li>
            <li>Save your mapping for future imports from the same source</li>
          </ol>

          <h4>Tips for Successful Mapping</h4>
          <ul>
            <li>
              <strong>Consistent naming:</strong> Use consistent column names in your exports
            </li>
            <li>
              <strong>Units conversion:</strong> Specify units during mapping (mph, kph, yards, meters)
            </li>
            <li>
              <strong>Save mappings:</strong> Save your mappings for each launch monitor you use
            </li>
            <li>
              <strong>Test with small files:</strong> Test your mapping with a small file before importing large datasets
            </li>
          </ul>
        </div>
      </div>

      <div className="format-section">
        <h3>Data Validation</h3>
        <p>
          RangeDat performs validation checks on imported data to ensure quality and consistency.
        </p>

        <div className="validation-info">
          <h4>Validation Checks</h4>
          <ul>
            <li>
              <strong>Range validation:</strong> Values must be within realistic ranges (e.g., ball speed between 50-200 mph)
            </li>
            <li>
              <strong>Consistency checks:</strong> Related values must be consistent (e.g., total distance ≥ carry distance)
            </li>
            <li>
              <strong>Missing data handling:</strong> Critical missing values will be flagged
            </li>
            <li>
              <strong>Duplicate detection:</strong> Duplicate shots will be identified
            </li>
          </ul>

          <h4>Handling Validation Issues</h4>
          <p>
            When validation issues are detected, RangeDat will:
          </p>
          <ul>
            <li>Display warnings for minor issues</li>
            <li>Allow you to correct or ignore flagged data</li>
            <li>Provide options to filter out problematic shots</li>
            <li>Suggest fixes for common data problems</li>
          </ul>
        </div>
      </div>
    </div>
  );
};

export default DataFormatGuide;
